import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../common/AuthContext";
import { getBrokerAccounts, getBrokerId } from "../../../service/api/BrokerAccountApi";
import { createStrategy, getDraft, updateDraft } from "./strategyStorage";
import "../AlgoTrading.css";

const steps = [
  { key: "broker", label: "Broker" },
  { key: "configure", label: "Configure" },
  { key: "conditions", label: "Conditions" },
  { key: "review", label: "Review" },
];

const paths = {
  broker: "/strategies/new/broker",
  configure: "/strategies/new/configure",
  conditions: "/strategies/new/conditions",
  review: "/strategies/new/review",
};

const isConnected = (status = "") =>
  ["SUCCESS", "CONNECTED", "ACTIVE", "VERIFIED"].includes(status.toUpperCase().replace(/[\s_-]/g, ""));

const StrategyWizard = ({ step }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draft, setDraft] = useState(getDraft);
  const [brokers, setBrokers] = useState([]);
  const [error, setError] = useState("");
  const stepIndex = steps.findIndex((item) => item.key === step);

  useEffect(() => {
    if (step !== "broker" || !user?.userId) return;
    getBrokerAccounts(user.userId)
      .then((data) => setBrokers((Array.isArray(data) ? data : []).filter((broker) => isConnected(broker.status))))
      .catch((requestError) => setError(requestError.response?.data?.message || "Unable to load connected brokers."));
  }, [step, user?.userId]);

  const change = (name, value) => {
    const next = updateDraft({ [name]: value });
    setDraft(next);
  };

  const selectBroker = (broker) => {
    const next = updateDraft({
      brokerId: getBrokerId(broker),
      brokerName: broker.accountName,
      brokerType: broker.brokerType,
    });
    setDraft(next);
  };

  const next = () => navigate(paths[steps[stepIndex + 1].key]);
  const back = () => stepIndex === 0 ? navigate("/strategies") : navigate(paths[steps[stepIndex - 1].key]);
  const save = (status) => {
    const strategy = createStrategy(draft, status);
    navigate(`/strategies/${strategy.id}`);
  };

  return (
    <main className="algo-page">
      <header className="algo-page-heading"><div><h1>Create New Strategy</h1><p>Configure your automated trading strategy.</p></div><span>Step {stepIndex + 1} of 4</span></header>
      <div className="algo-stepper">
        {steps.map((item, index) => <div className={index <= stepIndex ? "active" : ""} key={item.key}><span>{index + 1}</span>{item.label}</div>)}
      </div>

      <section className="algo-form-card">
        {step === "broker" && <>
          <h2>Select Broker Account</h2>
          <label className="algo-field"><span>Strategy Name</span><input value={draft.name || ""} onChange={(e) => change("name", e.target.value)} placeholder="Enter strategy name" /></label>
          {error && <div className="algo-error">{error}</div>}
          <div className="algo-broker-choice">
            {brokers.map((broker) => <button className={draft.brokerId === getBrokerId(broker) ? "selected" : ""} key={getBrokerId(broker)} onClick={() => selectBroker(broker)}>
              <i className="fa fa-briefcase"></i><strong>{broker.accountName}</strong><small>{broker.brokerType}</small>
            </button>)}
            {!error && brokers.length === 0 && <p>No connected brokers found. Connect a broker before creating a strategy.</p>}
          </div>
        </>}

        {step === "configure" && <>
          <h2>Configure Strategy</h2>
          <div className="algo-form-grid">
            <label className="algo-field"><span>Exchange</span><select value={draft.exchange || "NSE"} onChange={(e) => change("exchange", e.target.value)}><option>NSE</option><option>BSE</option></select></label>
            <label className="algo-field"><span>Symbol</span><input value={draft.symbol || ""} onChange={(e) => change("symbol", e.target.value)} placeholder="NIFTY" /></label>
            <label className="algo-field"><span>Quantity</span><input type="number" min="1" value={draft.quantity || ""} onChange={(e) => change("quantity", e.target.value)} /></label>
            <label className="algo-field"><span>Product Type</span><select value={draft.productType || "INTRADAY"} onChange={(e) => change("productType", e.target.value)}><option>INTRADAY</option><option>DELIVERY</option></select></label>
            <label className="algo-field"><span>Stop Loss (₹)</span><input type="number" value={draft.stopLoss || ""} onChange={(e) => change("stopLoss", e.target.value)} /></label>
            <label className="algo-field"><span>Target (₹)</span><input type="number" value={draft.target || ""} onChange={(e) => change("target", e.target.value)} /></label>
            <label className="algo-field"><span>Maximum Daily Loss (₹)</span><input type="number" value={draft.maxDailyLoss || ""} onChange={(e) => change("maxDailyLoss", e.target.value)} /></label>
            <label className="algo-field"><span>Trading Mode</span><select value={draft.tradingMode || "PAPER"} onChange={(e) => change("tradingMode", e.target.value)}><option>PAPER</option><option>LIVE</option></select></label>
          </div>
        </>}

        {step === "conditions" && <>
          <h2>Entry & Exit Conditions</h2>
          <div className="algo-form-grid">
            <label className="algo-field"><span>Entry Condition</span><select value={draft.entryType || "OI_INCREASE"} onChange={(e) => change("entryType", e.target.value)}><option value="OI_INCREASE">OI Increase</option><option value="PRICE_ABOVE">Price Above</option><option value="EMA_CROSSOVER">EMA Crossover</option></select></label>
            <label className="algo-field"><span>Entry Value</span><input type="number" value={draft.entryValue || ""} onChange={(e) => change("entryValue", e.target.value)} /></label>
            <label className="algo-field"><span>Time Frame</span><select value={draft.timeFrame || "3_MIN"} onChange={(e) => change("timeFrame", e.target.value)}><option value="1_MIN">1 Minute</option><option value="3_MIN">3 Minutes</option><option value="5_MIN">5 Minutes</option><option value="15_MIN">15 Minutes</option></select></label>
            <label className="algo-field"><span>Logic</span><select value={draft.logic || "AND"} onChange={(e) => change("logic", e.target.value)}><option>AND</option><option>OR</option></select></label>
            <label className="algo-field"><span>Exit Condition</span><select value={draft.exitType || "TARGET_OR_STOP"} onChange={(e) => change("exitType", e.target.value)}><option value="TARGET_OR_STOP">Target or Stop Loss</option><option value="TIME_BASED">Time Based</option><option value="SIGNAL_REVERSAL">Signal Reversal</option></select></label>
            <label className="algo-field"><span>Exit Time</span><input type="time" value={draft.exitTime || "15:15"} onChange={(e) => change("exitTime", e.target.value)} /></label>
          </div>
        </>}

        {step === "review" && <>
          <h2>Review & Save</h2>
          <div className="algo-review">
            {[
              ["Strategy", draft.name], ["Broker", draft.brokerName], ["Symbol", `${draft.exchange || "NSE"}:${draft.symbol || "—"}`],
              ["Quantity", draft.quantity], ["Product", draft.productType], ["Mode", draft.tradingMode],
              ["Stop Loss", `₹${draft.stopLoss || 0}`], ["Target", `₹${draft.target || 0}`],
              ["Entry", draft.entryType], ["Exit", draft.exitType],
            ].map(([label, value]) => <div key={label}><span>{label}</span><strong>{value || "—"}</strong></div>)}
          </div>
        </>}

        <footer className="algo-form-footer">
          <button className="algo-secondary" onClick={back}>Back</button>
          {step !== "review" ? <button className="algo-primary" onClick={next} disabled={step === "broker" && (!draft.name || !draft.brokerId)}>Next</button> : <div><button className="algo-secondary" onClick={() => save("DRAFT")}>Save Draft</button><button className="algo-primary" onClick={() => save("ACTIVE")}>Save & Activate</button></div>}
        </footer>
      </section>
    </main>
  );
};

export default StrategyWizard;
